import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  MenuItem,
  Button,
  Divider,
  Alert,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Build as BuildIcon,
  Assignment as AssignmentIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { CompanyToolToggle, CompanyTaskToggle } from '@/organism/BossBaby';
import { BossBabyToolDto, BossBabyTaskDto, BossBabyCompanySettingDto } from '@/dto/BossBabyDto';

interface CompanySettingsTemplateProps {
  companies: Array<{ comId: number; comNm: string }>;
  selectedCompanyId: number | null;
  settings: {
    tools: Array<BossBabyToolDto & { companySetting?: BossBabyCompanySettingDto }>;
    tasks: Array<BossBabyTaskDto & { companySetting?: BossBabyCompanySettingDto }>;
  };
  loading: boolean;
  onCompanySelect: (comId: number) => void;
  onToolToggle: (comId: number, toolId: number, isEnabled: boolean) => void;
  onTaskToggle: (comId: number, taskId: number, isEnabled: boolean) => void;
  onResetAll: () => void;
}

export const CompanySettingsTemplate: React.FC<CompanySettingsTemplateProps> = ({
  companies,
  selectedCompanyId,
  settings,
  loading,
  onCompanySelect,
  onToolToggle,
  onTaskToggle,
  onResetAll,
}) => {
  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const selectedCompany = companies.find(c => c.comId === selectedCompanyId);

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ mb: 1 }}>
          Boss Baby Company Settings
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Configure Boss Baby tools and tasks for individual companies
        </Typography>
      </Box>

      {/* Company Selection */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <BusinessIcon color="primary" />
          <Typography variant="h6">
            Company Selection
          </Typography>
        </Box>

        <TextField
          fullWidth
          select
          label="Select Company"
          value={selectedCompanyId || ''}
          onChange={(e) => onCompanySelect(Number(e.target.value))}
          disabled={loading}
          size="medium"
        >
          <MenuItem value="">
            <em>Please select a company</em>
          </MenuItem>
          {companies.map((company) => (
            <MenuItem key={company.comId} value={company.comId}>
              {company.comNm} (ID: {company.comId})
            </MenuItem>
          ))}
        </TextField>

        {selectedCompany && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Managing Boss Baby settings for: <strong>{selectedCompany.comNm}</strong>
          </Alert>
        )}
      </Paper>

      {/* Settings Content */}
      {selectedCompanyId && (
        <Paper sx={{ p: 3 }}>
          {loading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography>Loading company settings...</Typography>
            </Box>
          ) : (
            <>
              {/* Header Actions */}
              <Box sx={{ display: 'flex', justifyContent: 'between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6">
                  Boss Baby Configuration
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={onResetAll}
                  color="warning"
                >
                  Reset All to Global
                </Button>
              </Box>

              {/* Tabs */}
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs value={tabValue} onChange={handleTabChange}>
                  <Tab 
                    icon={<BuildIcon />} 
                    label={`Tools (${settings.tools.length})`} 
                    iconPosition="start"
                  />
                  <Tab 
                    icon={<AssignmentIcon />} 
                    label={`Tasks (${settings.tasks.length})`} 
                    iconPosition="start"
                  />
                </Tabs>
              </Box>

              {/* Tools Tab */}
              {tabValue === 0 && (
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Enable or disable Boss Baby tools for this company. 
                    Disabled tools will not be available for Boss Baby operations.
                  </Typography>

                  {settings.tools.length === 0 ? (
                    <Alert severity="info">
                      No Boss Baby tools found. Please create tools first.
                    </Alert>
                  ) : (
                    <Box>
                      {settings.tools.map((tool) => (
                        <CompanyToolToggle
                          key={tool.toolId}
                          tool={tool}
                          onToggle={(toolId, isEnabled) => 
                            onToolToggle(selectedCompanyId, toolId, isEnabled)
                          }
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              )}

              {/* Tasks Tab */}
              {tabValue === 1 && (
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Enable or disable Boss Baby tasks for this company. 
                    Disabled tasks will not be executed for this company's users.
                  </Typography>

                  {settings.tasks.length === 0 ? (
                    <Alert severity="info">
                      No Boss Baby tasks found. Please create tasks first.
                    </Alert>
                  ) : (
                    <Box>
                      {settings.tasks.map((task) => (
                        <CompanyTaskToggle
                          key={task.taskId}
                          task={task}
                          onToggle={(taskId, isEnabled) => 
                            onTaskToggle(selectedCompanyId, taskId, isEnabled)
                          }
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              )}

              {/* Summary */}
              <Divider sx={{ my: 3 }} />
              <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Configuration Summary
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 4 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Active Tools
                    </Typography>
                    <Typography variant="h6" color="success.main">
                      {settings.tools.filter(t => t.companySetting?.isEnabled ?? t.isActive).length} / {settings.tools.length}
                    </Typography>
                  </Box>
                  
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Active Tasks
                    </Typography>
                    <Typography variant="h6" color="success.main">
                      {settings.tasks.filter(t => t.companySetting?.isEnabled ?? t.isActive).length} / {settings.tasks.length}
                    </Typography>
                  </Box>
                  
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Custom Settings
                    </Typography>
                    <Typography variant="h6" color="info.main">
                      {[...settings.tools, ...settings.tasks].filter(item => item.companySetting).length}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </>
          )}
        </Paper>
      )}

      {/* Help Text */}
      <Paper sx={{ p: 3, mt: 3, bgcolor: 'grey.50' }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Company Settings Guide
        </Typography>
        
        <Typography variant="body2" paragraph>
          <strong>Global vs Company Settings:</strong> By default, all companies use global tool and task settings. 
          When you toggle a setting for a specific company, it creates a company-specific override.
        </Typography>
        
        <Typography variant="body2" paragraph>
          <strong>Tool Settings:</strong> Control which Boss Baby tools are available for the selected company. 
          Disabled tools cannot be used in any Boss Baby operations for that company.
        </Typography>
        
        <Typography variant="body2" paragraph>
          <strong>Task Settings:</strong> Control which Boss Baby tasks can be executed for the selected company. 
          Disabled tasks will not appear in the company's Boss Baby interface.
        </Typography>
        
        <Typography variant="body2">
          <strong>Reset to Global:</strong> Removes all company-specific overrides and reverts to global settings. 
          This action cannot be undone.
        </Typography>
      </Paper>
    </Box>
  );
};